import { FormEvent, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getUserChatState } from 'selectors/userChat';
import { postUserChat } from 'store/user_chat/slice';
import { Thread } from './types';
import { trackEvent } from 'helpers/mixpanelEvents';
import { LLMSectionUI } from './LLMSectionUI';
import { ModalComponent } from 'components/Modal';

const GENERIC_ERROR_ANSWER = 'Sorry, something went wrong. Please try again.';

const CONTEXT_HISTORY_TURNS = 2;

const getContextHistoryFromThread = (thread: Thread[]) => {
  if (thread.length < 1) {
    return [];
  }
  return thread
    .slice(-1 * CONTEXT_HISTORY_TURNS)
    .map(exchange => ({ prompt: exchange.question, response: exchange.answer }));
};

export const LLMSection = () => {
  const dispatch = useAppDispatch();
  const { data, error } = useAppSelector(getUserChatState);
  const [conversationId, setConversationId] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [thread, setThread] = useState<Thread[]>([]);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const newConversationId = uuidv4();
    setConversationId(newConversationId);
    trackEvent('Profile Dot Section Viewed', {
      conversation_id: newConversationId,
    });
  }, []);

  // Resolves the in-flight question against the redux chat state once
  // postUserChat settles — pending clears `data` to null, so a stale answer
  // from an earlier call can never be mistaken for this one's result.
  useEffect(() => {
    if (!pendingQuestion) return;
    if (data !== null) {
      setThread(previousThread => [
        ...previousThread,
        {
          question: pendingQuestion,
          answer: data.answer,
          donation_links: data.donation_links,
        },
      ]);
      setPendingQuestion(null);
    } else if (error !== null) {
      setThread(previousThread => [
        ...previousThread,
        { question: pendingQuestion, answer: GENERIC_ERROR_ANSWER },
      ]);
      setPendingQuestion(null);
    }
  }, [data, error, pendingQuestion]);

  const askQuestion = (question: string) => {
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion || pendingQuestion) return;
    setInputValue('');
    setPendingQuestion(trimmedQuestion);
    dispatch(
      postUserChat({
        prompt: trimmedQuestion,
        conversation_id: conversationId,
        context_history: getContextHistoryFromThread(thread),
      })
    );
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    askQuestion(inputValue);
  };

  const getOnLinkClick =
    (
      eventName:
        | 'Profile Dot Response Donate Link Clicked'
        | 'Profile Dot Response Inline Link Clicked',
      {
        linkUrl,
        question,
        response,
        conversationId,
      }: {
        linkUrl: string;
        question: string;
        response: string;
        conversationId: string;
      }
    ) =>
    () => {
      trackEvent(eventName, {
        link_url: linkUrl,
        raw_question: question,
        response_text: response,
        conversation_id: conversationId,
      });
    };

  const openModalIfNeeded = () => {
    if (window.matchMedia('(max-width: 1000px)').matches) {
      setIsModalOpen(true);
    }
  };

  const isSending = pendingQuestion !== null;

  return (
    <>
      <ModalComponent
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        isFullScreen
        contentLabel="DotLLM"
      >
        <div className="llm-modal-content">
          <LLMSectionUI
            {...{
              askQuestion,
              conversationId,
              getOnLinkClick,
              handleSubmit,
              inputValue,
              isSending,
              pendingQuestion,
              setInputValue,
              thread,
            }}
          />
        </div>
      </ModalComponent>
      <LLMSectionUI
        {...{
          askQuestion,
          conversationId,
          getOnLinkClick,
          handleSubmit,
          inputValue,
          isSending,
          openModalIfNeeded,
          pendingQuestion,
          setInputValue,
          thread,
        }}
      />
    </>
  );
};
